import { TouchEvent as ReactTouchEvent, useRef, useState } from 'react'
import classNames from 'classnames'
import { AccountCircleIcon } from '@/atoms/Icons/AccountCircleIcon'
import { UpArrowCircleIcon } from '@/atoms/Icons/UpArrowCircleIcon'
import { Image } from '@/atoms/Image'
import { ParagraphMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'

const HEADSHOT_CATEGORY = 'HEADSHOT'

export function CastRail() {
  const ref = useRef<HTMLDivElement>(null)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 50
  const { t } = useTranslate('theatrical-presales')
  const projectMeta = useProjectMeta()

  const cast = projectMeta.title?.cast?.filter(isDefined) ?? []

  if (cast.length === 0) return null

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    const start = event.targetTouches[0]?.clientX
    if (typeof start === 'number') setTouchStart(start)
  }

  const onTouchMove = (event: ReactTouchEvent) => {
    const end = event.targetTouches[0]?.clientX
    if (typeof end === 'number') setTouchEnd(end)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    isLeftSwipe ? handleRightDirection() : handleLeftDirection()
  }

  const handleLeftDirection = () => {
    const scrollLeft = ref?.current?.scrollLeft ?? 0
    let scrollTo

    if (scrollLeft - 240 <= 0) {
      scrollTo = 0
    } else {
      scrollTo = scrollLeft - 240
    }

    ref?.current?.scrollTo({ top: 0, left: scrollTo, behavior: 'smooth' })
  }

  const handleRightDirection = () => {
    const scrollLeft = ref?.current?.scrollLeft ?? 0
    let scrollTo

    if (scrollLeft + 240 <= 0) {
      scrollTo = 0
    } else {
      scrollTo = scrollLeft + 240
    }

    ref?.current?.scrollTo({ top: 0, left: scrollTo, behavior: 'smooth' })
  }

  return (
    <div>
      <div className="flex items-center justify-between pb-2">
        <TitleMD color="white" weight="semibold" className="uppercase">
          {t('filmCast', 'Cast')}
        </TitleMD>
        <div
          className={classNames('flex items-center gap-3', {
            'md:hidden': cast.length <= 3,
          })}
        >
          <div onClick={handleLeftDirection}>
            <UpArrowCircleIcon
              className="-rotate-90 cursor-pointer !stroke-white transition-all duration-300 ease-in-out hover:!stroke-white lg:!stroke-core-gray-300"
              color="inherit"
              size={32}
            />
          </div>
          <div onClick={handleRightDirection}>
            <UpArrowCircleIcon
              className="rotate-90 cursor-pointer !stroke-white transition-all duration-300 ease-in-out hover:!stroke-white lg:!stroke-core-gray-300"
              color="inherit"
              size={32}
            />
          </div>
        </div>
      </div>
      <div
        className="flex items-start gap-4 overflow-hidden"
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {cast.map((castMember, i) => {
          const headshotUrl = castMember.images?.find((img) => img?.category === HEADSHOT_CATEGORY)?.cloudinaryPath
          return (
            <div key={i} className="flex w-36 shrink-0 flex-col items-center justify-center">
              <div className="relative mx-auto h-[120px] w-[120px] overflow-hidden rounded-full">
                {headshotUrl ? (
                  <Image alt={castMember.name} src={headshotUrl} height={120} width={120} />
                ) : (
                  <div className="bg-core-gray-400">
                    <AccountCircleIcon aria-label="generic-profile-picture" size={120} />
                  </div>
                )}
              </div>
              <div className="w-full text-center">
                <ParagraphMD
                  color="white"
                  weight="bold"
                  className="overflow-hidden text-ellipsis whitespace-nowrap"
                  title={castMember.name}
                >
                  {castMember.name}
                </ParagraphMD>
                <ParagraphSM color="core-gray-300" className="line-clamp-2 overflow-hidden text-ellipsis">
                  {castMember.character}
                </ParagraphSM>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
